import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import KpiCard from '../../components/KpiCard';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import useCachedQuery from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import DataHealthPanel from '../../components/DataHealthPanel';
import IkDashboardWidget from '../../components/IkDashboardWidget';
import SiparisOneriWidget from '../../components/SiparisOneriWidget';
import { searchMatchPre } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const YonetimDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [arama, setArama] = useState('');
  const debouncedArama = useDebouncedValue(arama, 300);
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const {
    data: kullanicilar = [],
    isLoading: kullanicilarLoading,
    isValidating: kullanicilarValidating,
    error: usersError,
    revalidate: revalidateKullanicilar
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'yonetim', 'kullanicilar'],
    async () => {
      const res = await fetch(`${API_URL}/api/kullanicilar`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: özet = {},
    isLoading: ozetLoading,
    isValidating: ozetValidating,
    error: ozetError,
    revalidate: revalidateOzet
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'yonetim', 'özet'],
    async () => {
      const res = await fetch(`${API_URL}/api/raporlar/ozet`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data && typeof data === 'object' ? data : {};
    },
    { staleTime: 20000 }
  );

  const {
    data: bekleyenOnaylar = [],
    isLoading: bekleyenLoading,
    isValidating: bekleyenValidating,
    error: approvalsError,
    revalidate: revalidateBekleyen
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'yonetim', 'approvals'],
    async () => {
      const res = await fetch(`${API_URL}/api/approvals/pending`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: dashSummary = {},
    isLoading: summaryLoading,
    isValidating: summaryValidating,
    revalidate: revalidateSummary
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'yonetim', 'summary'],
    async () => {
      const res = await fetch(`${API_URL}/api/dashboard/summary`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data && typeof data === 'object' ? data : {};
    },
    { staleTime: 20000 }
  );

  const loading = kullanicilarLoading || ozetLoading || bekleyenLoading || summaryLoading;
  const isValidating = kullanicilarValidating || ozetValidating || bekleyenValidating || summaryValidating;
  const hata = usersError || ozetError || approvalsError ? 'Bazi veriler yüklenemedi.' : '';

  // Auto-refresh her 30 saniye
  const refreshAll = useCallback(() => {
    revalidateKullanicilar({ force: true }).catch(() => {});
    revalidateOzet({ force: true }).catch(() => {});
    revalidateBekleyen({ force: true }).catch(() => {});
    revalidateSummary({ force: true }).catch(() => {});
  }, [revalidateKullanicilar, revalidateOzet, revalidateBekleyen, revalidateSummary]);

  useEffect(() => {
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const toplamBorc = özet?.toplam_borc || 0;
  const borcDetay = özet?.borc_detay || {};
  const borcTooltip = [
    borcDetay.hesaplama_yontemi || 'Ödenmemiş fatura toplamı (KDV dahil)',
    '',
    `• Ödenecek: ${(borcDetay.odenecek_fatura_toplami || 0).toLocaleString('tr-TR')} TL`,
    `• Kısmi ödenen kalan: ${(borcDetay.kismi_odenen_kalan || 0).toLocaleString('tr-TR')} TL`,
    `• Vadesi geçmiş: ${(borcDetay.vadesi_gecmis_toplam || 0).toLocaleString('tr-TR')} TL`,
    `• Borçlu tedarikçi: ${borcDetay.borclu_tedarikci_sayisi || 0}`,
    `• Açık fatura: ${borcDetay.fatura_sayisi || 0}`
  ].join('\n');

  // Ciro verileri
  const posData = dashSummary.pos || {};
  const buAyCiro = posData.bu_ay_toplam || 0;
  const gecenAyCiro = posData.gecen_ay_toplam || 0;
  const ytdCiro = posData.ytd_toplam || 0;
  const yoyBuyume = posData.yoy_buyume_pct;
  const yediGunCiro = posData.yedi_gun_toplam || 0;

  const kritikStok = dashSummary.stok?.kritik_sayi || 0;
  const dusukStok = dashSummary.stok?.dusuk_sayi || 0;

  const filteredKullanicilar = useMemo(() => {
    const q = (debouncedArama || '').trim().toLowerCase();
    if (!q) return kullanicilar;
    return kullanicilar.filter(u =>
      searchMatchPre(u.ad, q) ||
      searchMatchPre(u.email, q) ||
      searchMatchPre(u.departman || u.rol, q)
    );
  }, [kullanicilar, debouncedArama]);

  return (
    <div>
      <Header title="Sistem Yonetim Panosu" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {hata && (
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: '6px', padding: '10px 14px', marginBottom: '14px' }}>
            {hata}
          </div>
        )}
        {/* Veri Sağlık Paneli */}
        <DataHealthPanel />

        {/* Sipariş Önerisi — kritik stoktaki malzemeler için tek tıkla bulk talep */}
        {user && hasPermission(user.role, PERMISSIONS.CREATE_REQUEST) && (
          <SiparisOneriWidget user={user} />
        )}

        {/* İK Özet */}
        <div style={{ marginBottom: 15 }}>
          <IkDashboardWidget />
        </div>

        {/* Üst Satır: Genel İşletme KPI'ları */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '15px' }}>
          <KpiCard
            title="Aktif Kullanıcı"
            value={loading ? '...' : kullanicilar.length}
            color={styles.success}
            icon="👥"
            subtitle="Sistem kullanıcıları"
          />
          <KpiCard
            title="Bekleyen Onay"
            value={loading ? '...' : bekleyenOnaylar.length}
            color={styles.warning}
            icon="⏳"
            subtitle="Yönetim onayı bekleyen"
            onClick={() => navigate('/satinalma#onaylar')}
          />
          <KpiCard
            title="Bekleyen Talep"
            value={loading ? '...' : (özet?.bekleyen_talep || 0)}
            color={styles.info}
            icon="📋"
            subtitle="Şubelerden gelen talep"
            onClick={() => navigate('/satinalma#talepler')}
          />
          {hasPermission(user.role, PERMISSIONS.VIEW_FINANCIAL_BORC) && (
            <KpiCard
              title="Toplam Borç"
              value={loading ? '...' : `${toplamBorc.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`}
              color={styles.primary}
              icon="💰"
              subtitle={`${borcDetay.borclu_tedarikci_sayisi || 0} tedarikçi · ${borcDetay.fatura_sayisi || 0} fatura`}
              tooltip={borcTooltip}
              onClick={() => navigate('/finans#cari-hesaplar')}
            />
          )}
        </div>

        {/* Ciro KPI Satırı (Bu Ay / Geçen Ay / YTD / 7 Gün) */}
        {hasPermission(user.role, PERMISSIONS.VIEW_FINANCIAL_CIRO) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '15px' }}>
            <KpiCard
              title="Bu Ay Ciro"
              value={loading ? '...' : `${buAyCiro.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`}
              color="#2e7d32"
              icon="📈"
              subtitle={`${new Date().toLocaleDateString('tr-TR', { month: 'long' })} · Ayın 1'inden bugüne`}
              tooltip={`Bu ayın başından (${new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('tr-TR')}) bugüne kadar sisteme yüklenen POS net cirolarının toplamıdır.`}
              trend={yoyBuyume != null ? { value: yoyBuyume, label: 'geçen yılın aynı ayına göre' } : null}
            />
            <KpiCard
              title="Geçen Ay Ciro"
              value={loading ? '...' : `${gecenAyCiro.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`}
              color="#1565c0"
              icon="📊"
              subtitle="Tam ay toplamı"
              tooltip="Geçen ayın tamamına ait POS net cirolarının toplamıdır."
            />
            <KpiCard
              title="YTD Ciro"
              value={loading ? '...' : `${ytdCiro.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`}
              color="#7b1fa2"
              icon="🗓️"
              subtitle={`Yıl başından bugüne (${new Date().getFullYear()})`}
              tooltip={`${new Date().getFullYear()} yılının 1 Ocak'ından bugüne kadar toplam POS net cirosu (Year-to-Date).`}
            />
            <KpiCard
              title="Son 7 Gün Ciro"
              value={loading ? '...' : `${yediGunCiro.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`}
              color="#f57c00"
              icon="📅"
              subtitle="Haftalık toplam"
              tooltip="Son 7 günün POS net cirolarının toplamıdır."
            />
          </div>
        )}

        {/* Stok KPI Satırı (Kritik / Düşük / A Sınıfı) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <KpiCard
            title="Kritik Stok"
            value={loading ? '...' : kritikStok}
            color={kritikStok > 0 ? styles.danger : styles.success}
            icon="🚨"
            subtitle={kritikStok > 0 ? `${kritikStok} ürün eşik altında` : 'Tüm stoklar sağlıklı'}
            tooltip={`Miktarı sıfır veya sıfırın altına düşmüş ürün sayısı.\n\nBu ürünler: aktif malzeme listesinde olup Stok.miktar ≤ 0 koşulunu sağlayan kayıtlar.\n\nÇözüm: İlgili ürün için sayım veya mal kabul yapılmalı.`}
            onClick={() => navigate('/stok-depo#stok-durumu')}
          />
          <KpiCard
            title="Düşük Stok"
            value={loading ? '...' : dusukStok}
            color={dusukStok > 0 ? '#f57c00' : styles.success}
            icon="⚠️"
            subtitle={dusukStok > 0 ? `${dusukStok} ürün min. seviyede` : 'Tüm stoklar yeterli'}
            tooltip="Miktarı 0'dan büyük ama malzeme kartında tanımlı min_stok değerine eşit veya altında olan ürün sayısı."
            onClick={() => navigate('/stok-depo#stok-durumu')}
          />
          <KpiCard
            title="ABC Sınıflı Ürün"
            value={loading ? '...' : Object.values(dashSummary.stok?.abc_dagilim || {}).reduce((s, v) => s + v, 0)}
            color="#7b1fa2"
            icon="🏷️"
            subtitle={
              dashSummary.stok?.abc_dagilim
                ? `A: ${dashSummary.stok.abc_dagilim.A || 0} · B: ${dashSummary.stok.abc_dagilim.B || 0} · C: ${dashSummary.stok.abc_dagilim.C || 0}`
                : 'ABC analizi henüz yapılmamış'
            }
            tooltip="Ciro payına göre sınıflandırılmış aktif ürün sayısı. A: yüksek cirolu / kritik, B: orta, C: düşük etki."
            onClick={() => navigate('/stok-depo#abc-analiz')}
          />
          {hasPermission(user.role, PERMISSIONS.VIEW_FINANCIAL_BORC) && (
            <KpiCard
              title="Haftalık Ödeme"
              value={loading ? '...' : `${(özet?.haftalik_odeme || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`}
              color="#d32f2f"
              icon="💳"
              subtitle="Bu hafta vadesi gelen"
              tooltip="Bu hafta içinde (Pazartesi - Pazar) vadesi gelen ödenmemiş faturaların toplam tutarıdır."
              onClick={() => navigate('/finans#odeme-plani')}
            />
          )}
        </div>

        {/* POS Ciro + Kritik Stok + Son Siparişler */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          {/* POS Günlük Ciro */}
          {hasPermission(user.role, PERMISSIONS.VIEW_FINANCIAL_CIRO) && (
            <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.sm }}>
              <h3 style={{ margin: '0 0 15px 0', color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>POS Günlük Ciro</h3>
              {(dashSummary.pos?.lokasyon_cirolar || []).length === 0 ? (
                <div style={{ color: colors.textSecondary, fontSize: '13px' }}>POS verisi yok</div>
              ) : (
                <div>
                  {(dashSummary.pos?.lokasyon_cirolar || []).map((l, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.border}` }}>
                      <span style={{ fontWeight: '600', fontSize: '13px' }}>{l.lokasyon_ad}</span>
                      <span style={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '14px' }}>
                        {(l.net_ciro || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} TL
                      </span>
                    </div>
                  ))}
                  <div style={{ marginTop: '10px', padding: '8px', background: '#e8f5e9', borderRadius: '6px', textAlign: 'center' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary }}>Son rapor: </span>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#2e7d32' }}>{dashSummary.pos?.son_rapor_tarihi || '-'}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Kritik Stok */}
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.sm }}>
            <h3 style={{ margin: '0 0 15px 0', color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>
              Kritik Stok
              {(dashSummary.stok?.kritik_sayi || 0) > 0 && (
                <span style={{ marginLeft: '8px', background: '#f8d7da', color: '#721c24', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>
                  {dashSummary.stok?.kritik_sayi}
                </span>
              )}
            </h3>
            {(dashSummary.stok?.kritik_urunler || []).length === 0 ? (
              <div style={{ color: '#2e7d32', fontSize: '13px' }}>Kritik stok yok</div>
            ) : (
              <div>
                {(dashSummary.stok?.kritik_urunler || []).slice(0, 5).map((u, idx) => (
                  <div key={idx} style={{ padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: '13px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '600' }}>
                        {u.malzeme_ad}
                        {u.abc_sinifi && (
                          <span style={{
                            display: 'inline-block', padding: '1px 5px', borderRadius: '3px',
                            fontSize: '9px', fontWeight: 'bold', marginLeft: '4px',
                            background: u.abc_sinifi === 'A' ? '#ffebee' : u.abc_sinifi === 'B' ? '#fff3e0' : '#e8f5e9',
                            color: u.abc_sinifi === 'A' ? '#c62828' : u.abc_sinifi === 'B' ? '#e65100' : '#2e7d32',
                          }}>
                            {u.abc_sinifi}
                          </span>
                        )}
                      </span>
                      <span style={{ color: '#721c24', fontWeight: 'bold' }}>{u.miktar} / {u.min_stok}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textSecondary }}>{u.lokasyon_ad}</div>
                  </div>
                ))}
                {(dashSummary.stok?.dusuk_sayi || 0) > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#856404' }}>
                    + {dashSummary.stok.dusuk_sayi} urun dusuk stokta
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Son Siparişler */}
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.sm }}>
            <h3 style={{ margin: '0 0 15px 0', color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>Son Siparişler</h3>
            {(dashSummary.son_siparisler || []).length === 0 ? (
              <div style={{ color: colors.textSecondary, fontSize: '13px' }}>Sipariş yok</div>
            ) : (
              <div>
                {(dashSummary.son_siparisler || []).map((s, idx) => (
                  <div key={idx} style={{ padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: '13px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600' }}>{s.siparis_no}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 'bold',
                        background: s.durum === 'TESLIM_ALINDI' ? '#d4edda' : s.durum === 'HAZIRLANIYOR' ? '#fff3cd' : '#e3f2fd',
                        color: s.durum === 'TESLIM_ALINDI' ? '#155724' : s.durum === 'HAZIRLANIYOR' ? '#856404' : '#1565c0'
                      }}>{s.durum}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                      <span style={{ fontSize: '11px', color: colors.textSecondary }}>{s.tedarikçi}</span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{(s.tutar || 0).toLocaleString('tr-TR')} TL</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '12px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>Kullanicilar</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  value={arama}
                  onChange={(e) => setArama(e.target.value)}
                  placeholder="Kullanıcı ara"
                  style={{ width: '220px', maxWidth: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', fontSize: '13px' }}
                />
                {isValidating && !loading && (
                  <span style={{ fontSize: '12px', color: colors.textSecondary, background: colors.backgroundAlt, borderRadius: '12px', padding: '4px 10px' }}>
                    Güncelleniyor...
                  </span>
                )}
              </div>
            </div>
            {loading ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <div key={`skeleton-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: `1px solid ${colors.border}` }}>
                    <div style={{ width: '65%' }}>
                      <div style={{ height: '12px', borderRadius: '4px', background: colors.border, marginBottom: '8px', width: '80%' }} />
                      <div style={{ height: '10px', borderRadius: '4px', background: colors.backgroundAlt, width: '60%' }} />
                    </div>
                    <div style={{ height: '24px', borderRadius: '12px', background: colors.border, width: '80px' }} />
                  </div>
                ))}
              </div>
            ) : filteredKullanicilar.length === 0 ? (
              <div style={{ color: colors.textSecondary, padding: '10px 0' }}>Kullanıcı verisi bulunamadı.</div>
            ) : (
              filteredKullanicilar.map((u) => (
                <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: `1px solid ${colors.border}` }}>
                  <div>
                    <strong>{u.ad || '-'}</strong>
                    <div style={{ fontSize: '12px', color: colors.textSecondary }}>{u.email || '-'}</div>
                  </div>
                  <span style={{ background: '#d4edda', color: '#155724', padding: '4px 10px', borderRadius: '4px', fontSize: '12px' }}>
                    {u.departman || u.rol || '-'}
                  </span>
                </div>
              ))
            )}
          </div>

          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>Sistem Islemleri</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => navigate('/master-veriler')} style={{ padding: '15px', background: styles.primary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>
                Master Verileri Düzenle
              </button>
              <button onClick={() => navigate('/yedekleme')} style={{ padding: '15px', background: styles.secondary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>
                Manuel Yedekleme Al
              </button>
              <button onClick={() => navigate('/log-kayitlari')} style={{ padding: '15px', background: styles.info, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>
                Log Kayitlarini Görüntüle
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YonetimDashboard;
